import type { QuestionnaireSource } from '@/lib/patientReports/model';

export type QuestionnaireFormChoice = {
  id: string;
  name: string;
  filled: boolean;
};

type FillSource = Pick<QuestionnaireSource, 'formsTemplateId' | 'name' | 'status' | 'completedAt'>;

/** A questionnaire the patient has answered. Sent or reminded copies are not filled. */
export function questionnaireResponseIsFilled(
  source: Pick<QuestionnaireSource, 'status' | 'completedAt'>,
): boolean {
  return source.status === 'completed' || source.completedAt != null;
}

function formIsFilled(form: { id: string; name: string }, filled: readonly FillSource[]): boolean {
  const name = form.name.trim();
  return filled.some((item) => {
    if (item.formsTemplateId) return item.formsTemplateId === form.id;
    const itemName = item.name.trim();
    return itemName !== '' && name !== '' && itemName === name;
  });
}

/**
 * Library order, with questionnaires this patient has filled placed first.
 * A chosen form that is no longer in the library stays selectable.
 */
export function orderQuestionnaireFormOptions(
  forms: readonly { id: string; name: string }[] | undefined,
  questionnaires: readonly FillSource[] | undefined,
  selected?: { id: string | null; name: string },
): QuestionnaireFormChoice[] {
  const filled = (questionnaires ?? []).filter(questionnaireResponseIsFilled);
  const seen = new Set<string>();
  const known: QuestionnaireFormChoice[] = [];
  for (const form of forms ?? []) {
    if (!form.id || seen.has(form.id)) continue;
    seen.add(form.id);
    known.push({ id: form.id, name: form.name, filled: formIsFilled(form, filled) });
  }
  const chosenId = selected?.id ?? null;
  if (chosenId && !seen.has(chosenId)) {
    const name = selected?.name.trim() || chosenId;
    known.push({
      id: chosenId,
      name,
      filled: formIsFilled({ id: chosenId, name }, filled),
    });
  }
  return [...known.filter((item) => item.filled), ...known.filter((item) => !item.filled)];
}
